import React, { useState, useMemo, useEffect } from "react";
import "./style.css";
import {
  getAllUsers,
  getOrganisation,
  getProviderByOrgName,
  getTrackerData,
} from "../../config/LiveApi";
import { useSelector } from "react-redux";

export default function WebhookTracker() {
  let token = useSelector((store) => store.userReducer.token);
  const [org, setOrg] = useState([]);
  const [provider, setProvider] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [orderId, setOrderId] = useState("");
  const [firstSetMessage, setFirstSetMessage] = useState("");
  const [orderSetMessage, setOrderSetMessage] = useState("");
  const [successToFailed, setSuccessToFailed] = useState([]);
  const [failedToSuccess, setFailedToSuccess] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  const [typeFilter, setTypeFilter] = useState({
    payin: true,
    payout: true,
  });

  function shortenOrderId(id) {
    if (!id) return "";
    if (id.length <= 12) return id;

    return `${id.slice(0, 6)}...${id.slice(-3)}`;
  }

  async function copyToClipboard(id) {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);

      setTimeout(() => {
        setCopiedId("");
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  }

  useEffect(() => {
    const fetchTrackerData = async () => {
      try {
        setLoading(true);
        const response = await getTrackerData({});

        setSuccessToFailed(response.successToFailed || []);
        setFailedToSuccess(response.failedToSuccess || []);
      } catch (err) {
        console.error("Initial tracker fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchOrgData = async () => {
      const orgResponse = await getOrganisation(token);
      setOrg(orgResponse.data);
    };

    const fetchUserData = async () => {
      const allUserData = await getAllUsers(token, 1);
      let adminUserData = [];
      allUserData.data.forEach((user) => {
        if (user.role === "admin") adminUserData.push(user);
      });
      setMerchants(adminUserData);
    };

    fetchTrackerData();
    fetchOrgData();
    fetchUserData();
  }, []);

  async function handleFirstSetSubmit(e) {
    e.preventDefault();

    const payload = {
      providerId: selectedProvider || null,
      orderId: orderId || null,
      userId: selectedMerchant || null,
    };

    try {
      setLoading(true);

      const response = await getTrackerData(payload);

      setSuccessToFailed(response.successToFailed || []);
      setFailedToSuccess(response.failedToSuccess || []);

      setFirstSetMessage("Results fetched successfully");
    } catch (err) {
      console.error(err);
      setFirstSetMessage("Failed to fetch data");
    } finally {
      setLoading(false);
      setTimeout(() => setFirstSetMessage(""), 3000);
    }
  }

  async function handleOrderSubmit(e) {
    e.preventDefault();
    if (!orderId.trim()) {
      setOrderSetMessage("Please enter an Order ID");
      setTimeout(() => setOrderSetMessage(""), 3000);
      return;
    }
    const payload = { orderId: orderId.trim() };

    const response = await getTrackerData(payload);

    setSuccessToFailed(response.successToFailed || []);
    setFailedToSuccess(response.failedToSuccess || []);

    setOrderSetMessage(`Searching for Order ID "${orderId}"`);
    setTimeout(() => setOrderSetMessage(""), 3500);
  }

  function handleOrgChange(e) {
    const value = e.target.value;
    setSelectedOrg(value);

    const fetchProviders = async () => {
      const providerResponse = await getProviderByOrgName(value);
      setProvider(providerResponse.data);
    };

    fetchProviders();
    setSelectedProvider("");
  }

  function applyTypeFilter(data) {
    return data.filter((item) => {
      const type = (item.webhookType || "").toLowerCase();

      if (!typeFilter.payin && !typeFilter.payout) return false;
      if (type === "payin" && typeFilter.payin) return true;
      if (type === "payout" && typeFilter.payout) return true;

      return false;
    });
  }

  const filteredSuccessToFailed = applyTypeFilter(successToFailed);
  const filteredFailedToSuccess = applyTypeFilter(failedToSuccess);

  return (
    <div className="searchPage-container">
      <div className="searchPage-card">
        <section className="searchPage-body">
          <form className="searchPage-form" onSubmit={handleFirstSetSubmit}>
            <div className="searchPage-formGrid">
              <label className="searchPage-field">
                <span className="searchPage-fieldLabel">Organization</span>
                <select
                  className="searchPage-select searchPage-selectOrg"
                  value={selectedOrg}
                  onChange={handleOrgChange}
                >
                  <option value="">Select organization</option>
                  {org.map((o) => (
                    <option key={o.id} value={o.orgName}>
                      {o.orgName}
                    </option>
                  ))}
                </select>
              </label>

              <label className="searchPage-field">
                <span className="searchPage-fieldLabel">Provider</span>
                <select
                  className="searchPage-select searchPage-selectProvider"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={!selectedOrg}
                  aria-disabled={!selectedOrg}
                  title={
                    !selectedOrg
                      ? "Choose an organization first"
                      : "Select provider"
                  }
                >
                  <option value="">
                    {selectedOrg
                      ? "Select provider"
                      : "Select organization first"}
                  </option>
                  {provider.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.providerName}
                    </option>
                  ))}
                </select>
              </label>

              <label className="searchPage-field">
                <span className="searchPage-fieldLabel">Merchant</span>
                <select
                  className="searchPage-select searchPage-selectMerchant"
                  value={selectedMerchant}
                  onChange={(e) => setSelectedMerchant(e.target.value)}
                >
                  <option value="">Select merchant</option>
                  {merchants.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.alias}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="searchPage-actionsRow">
              <button
                type="submit"
                className="searchPage-button searchPage-buttonPrimary"
                aria-label="Search with selected filters"
              >
                Search
              </button>

              <button
                type="button"
                className="searchPage-button searchPage-buttonSecondary"
                onClick={() => {
                  setSelectedOrg("");
                  setSelectedProvider("");
                  setSelectedMerchant("");
                }}
              >
                Reset
              </button>

              <div className="searchPage-inlineNote">{firstSetMessage}</div>
            </div>
          </form>

          <hr className="searchPage-divider" />

          <form
            className="searchPage-form searchPage-orderForm"
            onSubmit={handleOrderSubmit}
          >
            <div className="searchPage-orderRow">
              <label className="searchPage-field searchPage-orderField">
                <span className="searchPage-fieldLabel">Order ID</span>
                <input
                  className="searchPage-input searchPage-orderInput"
                  placeholder="e.g. 5313637tppg32x"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                />
              </label>

              <div className="searchPage-orderButtons">
                <button
                  type="submit"
                  className="searchPage-button searchPage-buttonPrimary"
                >
                  Search Order
                </button>
                <button
                  type="button"
                  className="searchPage-button searchPage-buttonGhost"
                  onClick={() => setOrderId("")}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="searchPage-inlineNote">{orderSetMessage}</div>
          </form>

          <div className="searchPage-filterBar">
            <div className="searchPage-filterTitle">Filter by Type</div>

            <label className="searchPage-filterItem">
              <input
                type="checkbox"
                checked={typeFilter.payin}
                onChange={() =>
                  setTypeFilter((prev) => ({ ...prev, payin: !prev.payin }))
                }
              />
              <span>Payin</span>
            </label>

            <label className="searchPage-filterItem">
              <input
                type="checkbox"
                checked={typeFilter.payout}
                onChange={() =>
                  setTypeFilter((prev) => ({ ...prev, payout: !prev.payout }))
                }
              />
              <span>Payout</span>
            </label>
          </div>

          {loading && (
            <div className="searchPage-loading">
              Fetching webhook tracker data...
            </div>
          )}

          {(successToFailed.length > 0 ||
            failedToSuccess.length > 0 ||
            loading) && (
            <div className="searchPage-resultsWrapper">
              <div className="searchPage-resultsColumn">
                <div className="searchPage-resultsHeader searchPage-resultsHeaderDanger">
                  Success → Failed ({filteredSuccessToFailed.length})
                </div>

                <div className="searchPage-tableContainer">
                  <table className="searchPage-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Provider ID</th>
                        <th>User ID</th>
                        <th>Type</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSuccessToFailed.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="searchPage-empty">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        filteredSuccessToFailed.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className="searchPage-copyId"
                                onClick={() => copyToClipboard(item.orderId)}
                                title="Click to copy full Order ID"
                              >
                                {shortenOrderId(item.orderId)}

                                <span className="searchPage-copyIcon">
                                  {copiedId === item.orderId ? "✓" : "📋"}
                                </span>
                              </span>
                            </td>
                            <td>{item.providerId}</td>
                            <td>{item.userId}</td>
                            <td className="searchPage-typeDanger">
                              {item.webhookType}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="searchPage-resultsColumn">
                <div className="searchPage-resultsHeader searchPage-resultsHeaderSuccess">
                  Failed → Success ({filteredFailedToSuccess.length})
                </div>

                <div className="searchPage-tableContainer">
                  <table className="searchPage-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Provider ID</th>
                        <th>User ID</th>
                        <th>Type</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredFailedToSuccess.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="searchPage-empty">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        filteredFailedToSuccess.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className="searchPage-copyId"
                                onClick={() => copyToClipboard(item.orderId)}
                                title="Click to copy full Order ID"
                              >
                                {shortenOrderId(item.orderId)}

                                <span className="searchPage-copyIcon">
                                  {copiedId === item.orderId ? "✓" : "📋"}
                                </span>
                              </span>
                            </td>
                            <td>{item.providerId}</td>
                            <td>{item.userId}</td>
                            <td className="searchPage-typeSuccess">
                              {item.webhookType}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
